/**
 * ============================================================================
 * SAVINGS GOALS v3.0 - TOKEN CONTEXT INTEGRATION
 * ============================================================================
 *
 * ✅ v3.0: TOKEN CONTEXT - Verwendet zentrales Token-Management
 *          - useToken() statt useAuth().getAccessToken()
 *          - Kein individueller Token-Fetch mehr
 *          - Verhindert Re-Render Loop & Token-Fehler
 * ✅ v2.0: PLANIVIO Rebranding + Token Fix
 * ✅ v1.0: Original Implementation
 *
 * Features:
 * - Sparziele anzeigen mit Progress-Bar
 * - Neues Sparziel erstellen
 * - Einzahlung auf Sparziel
 * - Ziel bearbeiten/löschen
 *
 * @version 3.0.0 - Token Context Integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useToken } from '../../context/TokenContext'; // ✅ v3.0: NEU!
import './SavingsGoals.css';

// ============================================================================
// CONFIGURATION
// ============================================================================
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ✅ v2.0: PLANIVIO FARBEN
const PLANIVIO = {
  teal: '#4A90A4',
  green: '#6BA893',
  warm: '#E8976C',
  border: '#B8D4DC',
  borderLight: '#D4E8EC',
  bgLight: '#E8F4F8',
  bgCard: '#F0F9FF',
  bgPage: '#F0F9FF',
  text: '#1F2937',
  textMuted: '#6B7280',
  gradient: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
  shadow: 'rgba(74, 144, 164, 0.3)',
  success: '#22C55E',
  warning: '#F59E0B',
  danger: '#EF4444'
};

console.log('🎯 SavingsGoals.js v3.0 loaded - TOKEN CONTEXT INTEGRATION 🔐');

const SavingsGoals = () => {
  // ✅ v3.0: useAuth NUR für isAuthenticated, NICHT für getAccessToken!
  const { isAuthenticated } = useAuth();

  // ✅ v3.0: Token aus zentralem Context!
  const { token, loading: tokenLoading } = useToken();
  
  // State
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [filter, setFilter] = useState('active'); // active, completed, all

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    target_amount: '',
    monthly_contribution: '',
    target_date: '',
    icon: '🎯',
    color: PLANIVIO.teal, // ✅ v2.0: Planivio Teal als Default
    priority: 1
  });

  const [contributeAmount, setContributeAmount] = useState('');
  const [contributeNote, setContributeNote] = useState('');

  // Icons für Sparziele
  const goalIcons = ['🎯', '🏠', '🚗', '✈️', '💻', '📱', '🎓', '💍', '🏥', '🎁', '💰', '🏖️'];
  // ✅ v2.0: Planivio-freundliche Farbpalette
  const goalColors = [
    PLANIVIO.teal,    // Planivio Primary
    PLANIVIO.green,   // Planivio Secondary
    '#3B82F6',        // Blue
    '#8B5CF6',        // Purple
    '#F59E0B',        // Amber
    '#EC4899',        // Pink
    '#06B6D4',        // Cyan
    '#84CC16'         // Lime
  ];

  // ============================================================================
  // ✅ v3.0: API Helper mit Token aus Context
  // ============================================================================
  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    if (!token) {
      throw new Error('Kein Token verfügbar');
    }

    try {
      console.log('🔑 SavingsGoals: Using token from TokenContext');

      const response = await fetch(`${API_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API Fehler: ${response.status}`);
      }

      return response.json();
    } catch (err) {
      console.error('❌ API Fehler:', err);
      throw err;
    }
  }, [token]);

  // Sparziele laden
  const loadGoals = useCallback(async () => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const data = await fetchWithAuth(`/api/budget/savings${statusParam}`);
      setGoals(data.savings || []);
    } catch (err) {
      console.error('❌ Fehler beim Laden der Sparziele:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token, fetchWithAuth, filter]);

  useEffect(() => {
    if (!tokenLoading && token) {
      loadGoals();
    }
  }, [loadGoals, tokenLoading, token]);

  // Sparziel erstellen
  const handleCreate = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      await fetchWithAuth('/api/budget/savings', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          target_amount: parseFloat(formData.target_amount),
          monthly_contribution: parseFloat(formData.monthly_contribution) || 0,
          priority: parseInt(formData.priority)
        })
      });
      
      setShowCreateModal(false);
      resetForm();
      loadGoals();
    } catch (err) {
      setError(err.message);
    }
  };

  // Einzahlung
  const handleContribute = async (e) => {
    e.preventDefault();
    
    if (!selectedGoal) return;
    
    try {
      setError(null);
      await fetchWithAuth(`/api/budget/savings/${selectedGoal.id}/contribute`, {
        method: 'POST',
        body: JSON.stringify({
          amount: parseFloat(contributeAmount),
          note: contributeNote
        })
      });
      
      setShowContributeModal(false);
      setContributeAmount('');
      setContributeNote('');
      setSelectedGoal(null);
      loadGoals();
    } catch (err) {
      setError(err.message);
    }
  };

  // Sparziel löschen
  const handleDelete = async (goalId) => {
    if (!window.confirm('Sparziel wirklich löschen?')) return;
    
    try {
      setError(null);
      await fetchWithAuth(`/api/budget/savings/${goalId}`, {
        method: 'DELETE'
      });
      loadGoals();
    } catch (err) {
      setError(err.message);
    }
  };

  // Form zurücksetzen
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      target_amount: '',
      monthly_contribution: '',
      target_date: '',
      icon: '🎯',
      color: PLANIVIO.teal,
      priority: 1
    });
  };

  // Einzahlung Modal öffnen
  const openContributeModal = (goal) => {
    setSelectedGoal(goal);
    setShowContributeModal(true);
  };

  // Formatierung
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  // ============================================================================
  // ✅ v2.0: PLANIVIO STYLES
  // ============================================================================
  const styles = {
    container: {
      padding: '2rem',
      maxWidth: '1200px',
      margin: '0 auto',
      background: PLANIVIO.bgPage,
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1.5rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    headerIcon: {
      width: '56px',
      height: '56px',
      background: PLANIVIO.gradient,
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.75rem',
      boxShadow: `0 4px 12px ${PLANIVIO.shadow}`
    },
    headerTitle: {
      margin: 0,
      fontSize: '1.75rem',
      fontWeight: 700,
      color: PLANIVIO.text
    },
    headerSubtitle: {
      margin: '0.25rem 0 0 0',
      color: PLANIVIO.textMuted,
      fontSize: '0.9rem'
    },
    createBtn: {
      padding: '0 28px',
      minHeight: '52px',
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      boxShadow: `0 4px 12px ${PLANIVIO.shadow}`,
      transition: 'all 0.2s'
    },
    filterBar: {
      display: 'flex',
      gap: '0.5rem',
      marginBottom: '1.5rem',
      background: 'white',
      padding: '0.25rem',
      borderRadius: '12px',
      border: `2px solid ${PLANIVIO.border}`,
      width: 'fit-content'
    },
    filterBtn: {
      padding: '0.5rem 1rem',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '0.875rem',
      fontWeight: 600,
      transition: 'all 0.2s'
    },
    filterBtnActive: {
      background: PLANIVIO.gradient,
      color: 'white'
    },
    filterBtnInactive: {
      background: 'transparent',
      color: PLANIVIO.textMuted
    },
    errorBanner: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: '#FEF2F2',
      border: '2px solid #FECACA',
      borderRadius: '12px',
      marginBottom: '1.5rem',
      color: '#991B1B'
    },
    errorCloseBtn: {
      background: 'none',
      border: 'none',
      fontSize: '1.25rem',
      cursor: 'pointer',
      color: '#991B1B'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '0.75rem'
    },
    card: {
      background: 'white',
      borderRadius: '10px',
      border: `1px solid ${PLANIVIO.border}`,
      padding: '0.6rem 0.75rem',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cardCompleted: {
      borderColor: PLANIVIO.success,
      background: `linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, white 100%)`
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem'
    },
    goalIcon: {
      width: '30px',
      height: '30px',
      borderRadius: '7px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.95rem',
      flexShrink: 0
    },
    goalInfo: {
      flex: 1,
      minWidth: 0
    },
    goalName: {
      fontWeight: 700,
      fontSize: '0.85rem',
      color: PLANIVIO.text,
      marginBottom: '0.1rem'
    },
    goalDescription: {
      fontSize: '0.85rem',
      color: PLANIVIO.textMuted,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    deleteBtn: {
      width: '32px',
      height: '32px',
      background: PLANIVIO.bgLight,
      border: `2px solid ${PLANIVIO.border}`,
      borderRadius: '8px',
      padding: 0,
      cursor: 'pointer',
      fontSize: '1rem',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      flexShrink: 0
    },
    progressSection: {
      marginBottom: '1rem'
    },
    progressAmounts: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.5rem'
    },
    progressCurrent: {
      fontWeight: 700,
      fontSize: '1.1rem',
      color: PLANIVIO.text
    },
    progressTarget: {
      fontSize: '0.9rem',
      color: PLANIVIO.textMuted
    },
    progressBar: {
      height: '12px',
      background: PLANIVIO.borderLight,
      borderRadius: '6px',
      overflow: 'hidden',
      marginBottom: '0.5rem'
    },
    progressFill: {
      height: '100%',
      borderRadius: '6px',
      transition: 'width 0.5s ease'
    },
    progressInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '0.8rem'
    },
    progressPercentage: {
      fontWeight: 700,
      color: PLANIVIO.teal
    },
    progressRemaining: {
      color: PLANIVIO.textMuted
    },
    goalMeta: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginBottom: '1rem'
    },
    metaItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      fontSize: '0.8rem',
      color: PLANIVIO.textMuted
    },
    contributeBtn: {
      width: '100%',
      padding: '0 28px',
      minHeight: '52px',
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      boxShadow: `0 4px 12px ${PLANIVIO.shadow}`
    },
    completedBadge: {
      width: '100%',
      padding: '0.875rem',
      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      color: 'white',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 700,
      textAlign: 'center',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem'
    },
    emptyState: {
      gridColumn: '1 / -1',
      padding: '4rem 2rem',
      textAlign: 'center',
      background: 'white',
      borderRadius: '20px',
      border: `2px dashed ${PLANIVIO.border}`
    },
    emptyIcon: {
      fontSize: '4rem',
      display: 'block',
      marginBottom: '1rem'
    },
    emptyTitle: {
      color: PLANIVIO.text,
      marginBottom: '0.5rem',
      fontSize: '1.25rem',
      fontWeight: 700
    },
    emptyText: {
      color: PLANIVIO.textMuted,
      marginBottom: '1.5rem'
    },
    // Modal Styles
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    },
    modal: {
      background: 'white',
      borderRadius: '20px',
      width: '100%',
      maxWidth: '500px',
      maxHeight: '90vh',
      overflow: 'hidden',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      border: `2px solid ${PLANIVIO.border}`
    },
    modalHeader: {
      padding: '1.25rem 1.5rem',
      borderBottom: `2px solid ${PLANIVIO.border}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      background: PLANIVIO.bgLight
    },
    modalTitle: {
      margin: 0,
      fontSize: '1.1rem',
      fontWeight: 700,
      color: PLANIVIO.text
    },
    modalCloseBtn: {
      width: '32px',
      height: '32px',
      border: `2px solid ${PLANIVIO.border}`,
      background: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '1.25rem',
      color: PLANIVIO.textMuted,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      justifyContent: 'center'
    },
    modalBody: {
      padding: '1.5rem',
      overflowY: 'auto',
      maxHeight: 'calc(90vh - 180px)'
    },
    modalActions: {
      padding: '1.25rem 1.5rem',
      borderTop: `2px solid ${PLANIVIO.border}`,
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      background: '#F9FAFB'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    formLabel: {
      display: 'block',
      fontWeight: 600,
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      color: PLANIVIO.text
    },
    formInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: `2px solid ${PLANIVIO.border}`,
      borderRadius: '10px',
      fontSize: '1rem',
      color: PLANIVIO.text,
      background: '#F9FAFB',
      outline: 'none',
      boxSizing: 'border-box',
      transition: 'border-color 0.2s'
    },
    formRow: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    },
    iconPicker: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    iconOption: {
      width: '44px',
      height: '44px',
      border: `2px solid ${PLANIVIO.border}`,
      borderRadius: '12px',
      background: 'white',
      fontSize: '1.25rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      padding: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    iconOptionSelected: {
      borderColor: PLANIVIO.teal,
      background: PLANIVIO.bgLight
    },
    colorPicker: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem'
    },
    colorOption: {
      width: '36px',
      height: '36px',
      border: '3px solid transparent',
      borderRadius: '50%',
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    colorOptionSelected: {
      borderColor: PLANIVIO.text,
      boxShadow: '0 0 0 2px white'
    },
    btnPrimary: {
      padding: '0 28px',
      minHeight: '52px',
      background: PLANIVIO.gradient,
      color: 'white',
      border: 'none',
      borderRadius: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '0.95rem',
      boxShadow: '0 4px 16px rgba(74, 144, 164, 0.25)',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    btnSecondary: {
      padding: '0 20px',
      minHeight: '44px',
      background: 'white',
      color: PLANIVIO.textMuted,
      border: `2px solid ${PLANIVIO.border}`,
      borderRadius: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '0.9rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    btnSuccess: {
      padding: '0 20px',
      minHeight: '44px',
      background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontWeight: 600,
      cursor: 'pointer',
      fontSize: '0.9rem',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      boxSizing: 'border-box'
    },
    contributeInfo: {
      padding: '1rem',
      background: PLANIVIO.bgLight,
      borderRadius: '12px',
      marginBottom: '1.5rem',
      border: `2px solid ${PLANIVIO.border}`
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '400px',
      gap: '1rem',
      color: PLANIVIO.textMuted
    },
    spinner: {
      width: '48px',
      height: '48px',
      border: `4px solid ${PLANIVIO.borderLight}`,
      borderTopColor: PLANIVIO.teal,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }
  };

  // Loading State
  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <p>Sparziele werden geladen...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Card-Layout Styles (konsistent mit BudgetTransactions)
  const cardStyle = {
    background: 'white',
    borderRadius: '20px',
    border: `2px solid ${PLANIVIO.border}`,
    boxShadow: '0 2px 8px rgba(74,144,164,0.1)',
    overflow: 'hidden'
  };
  const cardHeaderStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.45rem 1.25rem',
    borderBottom: `1px solid ${PLANIVIO.borderLight}`,
    background: PLANIVIO.bgLight
  };
  const cardTitleStyle = {
    margin: 0,
    fontSize: '1.1rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    color: PLANIVIO.text
  };
  return (
    <div style={cardStyle}>
      {/* Card Header */}
      <div style={cardHeaderStyle}>
        <h2 style={cardTitleStyle}><span>🎯</span> Sparziele</h2>
      </div>

      {/* Filter Row */}
      <div style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #D4E8EC', background: '#FAFCFE' }}>
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
          {['active', 'completed', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              style={{
                flex: 1,
                padding: '0.5rem 0.75rem',
                border: filter === f ? '2px solid #4A90A4' : '2px solid #B8D4DC',
                borderRadius: '10px',
                fontSize: '0.82rem',
                background: filter === f ? 'rgba(74,144,164,0.1)' : '#F0F9FF',
                cursor: 'pointer',
                color: filter === f ? '#4A90A4' : '#1F2937',
                fontWeight: filter === f ? 600 : 500,
                outline: 'none',
                transition: 'all 0.2s',
                boxSizing: 'border-box'
              }}
            >
              {f === 'active' ? 'Aktiv' : f === 'completed' ? 'Erreicht' : 'Alle'}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '1.25rem 1.5rem' }}>
        {/* Error */}
        {error && (
          <div style={{ ...styles.errorBanner, marginBottom: '1rem' }}>
            <span>❌ {error}</span>
            <button style={styles.errorCloseBtn} onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {/* Goals Grid */}
        <div style={styles.grid}>
        {goals.length === 0 ? (
          <div style={styles.emptyState}>
            <span style={styles.emptyIcon}>🎯</span>
            <h3 style={styles.emptyTitle}>Keine Sparziele</h3>
            <p style={styles.emptyText}>Erstelle dein erstes Sparziel!</p>
            <button 
              style={styles.createBtn}
              onClick={() => setShowCreateModal(true)}
            >
              <span>➕</span> Sparziel erstellen
            </button>
          </div>
        ) : (
          goals.map(goal => (
            <div 
              key={goal.id} 
              style={{
                ...styles.card,
                ...(goal.status === 'completed' ? styles.cardCompleted : {}),
                borderColor: goal.color || PLANIVIO.border
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = `0 12px 30px ${PLANIVIO.shadow}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Card Header */}
              <div style={styles.cardHeader}>
                <div style={{
                  ...styles.goalIcon,
                  backgroundColor: (goal.color || PLANIVIO.teal) + '20',
                  color: goal.color || PLANIVIO.teal
                }}>
                  {goal.icon || '🎯'}
                </div>
                <div style={styles.goalInfo}>
                  <div style={styles.goalName}>{goal.name}</div>
                  {goal.description && (
                    <div style={styles.goalDescription}>{goal.description}</div>
                  )}
                </div>
                <button
                  className="savings-delete-mini"
                  style={styles.deleteBtn}
                  onClick={(e) => { e.stopPropagation(); handleDelete(goal.id); }}
                  title="Löschen"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.danger;
                    e.currentTarget.style.background = '#FEF2F2';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = PLANIVIO.border;
                    e.currentTarget.style.background = PLANIVIO.bgLight;
                  }}
                >
                  🗑️
                </button>
              </div>

              {/* Progress */}
              <div style={styles.progressSection}>
                <div style={styles.progressAmounts}>
                  <span style={styles.progressCurrent}>{formatCurrency(goal.current_amount)}</span>
                  <span style={styles.progressTarget}>von {formatCurrency(goal.target_amount)}</span>
                </div>
                <div style={styles.progressBar}>
                  <div 
                    style={{
                      ...styles.progressFill,
                      width: `${Math.min(goal.percentage || 0, 100)}%`,
                      background: goal.status === 'completed' 
                        ? 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)'
                        : `linear-gradient(135deg, ${goal.color || PLANIVIO.teal} 0%, ${PLANIVIO.green} 100%)`
                    }}
                  />
                </div>
                <div style={styles.progressInfo}>
                  <span style={styles.progressPercentage}>{goal.percentage || 0}%</span>
                  {goal.remaining > 0 && (
                    <span style={styles.progressRemaining}>Noch {formatCurrency(goal.remaining)}</span>
                  )}
                </div>
              </div>

              {/* Meta */}
              <div style={styles.goalMeta}>
                {goal.target_date && (
                  <div style={styles.metaItem}>
                    <span>📅</span>
                    <span>{formatDate(goal.target_date)}</span>
                    {goal.days_left !== null && goal.days_left >= 0 && (
                      <span style={{ color: PLANIVIO.teal, fontWeight: 600 }}>
                        ({goal.days_left} Tage)
                      </span>
                    )}
                  </div>
                )}
                {goal.monthly_contribution > 0 && (
                  <div style={styles.metaItem}>
                    <span>💳</span>
                    <span>{formatCurrency(goal.monthly_contribution)}/Monat</span>
                  </div>
                )}
              </div>

              {/* Action */}
              {goal.status === 'active' ? (
                <button 
                  style={styles.contributeBtn}
                  onClick={(e) => { e.stopPropagation(); openContributeModal(goal); }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = `0 6px 20px ${PLANIVIO.shadow}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 4px 12px ${PLANIVIO.shadow}`;
                  }}
                >
                  <span>💰</span> Einzahlen
                </button>
              ) : (
                <div style={styles.completedBadge}>
                  <span>🎉</span> Ziel erreicht!
                </div>
              )}
            </div>
          ))
        )}
        </div>{/* closes goals grid */}
      </div>{/* closes content padding */}

      {/* Create Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>🎯 Neues Sparziel</h3>
              <button className="savings-close-mini" style={styles.modalCloseBtn} onClick={() => setShowCreateModal(false)}>✕</button>
            </div>
            
            <form onSubmit={handleCreate}>
              <div style={styles.modalBody}>
                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Name *</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. Urlaub 2025"
                    required
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Beschreibung</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    value={formData.description}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    placeholder="Optional"
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  />
                </div>

                <div style={styles.formRow}>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Zielbetrag * (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      style={styles.formInput}
                      value={formData.target_amount}
                      onChange={e => setFormData({...formData, target_amount: e.target.value})}
                      placeholder="1000.00"
                      required
                      onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                      onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Monatl. Sparrate (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      style={styles.formInput}
                      value={formData.monthly_contribution}
                      onChange={e => setFormData({...formData, monthly_contribution: e.target.value})}
                      placeholder="100.00"
                      onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                      onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Zieldatum</label>
                  <input
                    type="date"
                    style={styles.formInput}
                    value={formData.target_date}
                    onChange={e => setFormData({...formData, target_date: e.target.value})}
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Icon</label>
                  <div style={styles.iconPicker}>
                    {goalIcons.map(icon => (
                      <button
                        key={icon}
                        type="button"
                        style={{
                          ...styles.iconOption,
                          ...(formData.icon === icon ? styles.iconOptionSelected : {})
                        }}
                        onClick={() => setFormData({...formData, icon})}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Farbe</label>
                  <div style={styles.colorPicker}>
                    {goalColors.map(color => (
                      <button
                        key={color}
                        type="button"
                        style={{
                          ...styles.colorOption,
                          backgroundColor: color,
                          ...(formData.color === color ? styles.colorOptionSelected : {})
                        }}
                        onClick={() => setFormData({...formData, color})}
                      />
                    ))}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Priorität</label>
                  <select
                    style={styles.formInput}
                    value={formData.priority}
                    onChange={e => setFormData({...formData, priority: e.target.value})}
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  >
                    <option value="1">Normal</option>
                    <option value="2">Hoch</option>
                    <option value="3">Sehr hoch</option>
                  </select>
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowCreateModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" style={styles.btnPrimary}>
                  💾 Sparziel erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Contribute Modal */}
      {showContributeModal && selectedGoal && (
        <div style={styles.modalOverlay} onClick={() => setShowContributeModal(false)}>
          <div style={{...styles.modal, maxWidth: '400px'}} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>💰 Einzahlung</h3>
              <button className="savings-close-mini" style={styles.modalCloseBtn} onClick={() => setShowContributeModal(false)}>✕</button>
            </div>
            
            <form onSubmit={handleContribute}>
              <div style={styles.modalBody}>
                <div style={styles.contributeInfo}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '2rem' }}>{selectedGoal.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, color: PLANIVIO.text }}>{selectedGoal.name}</div>
                      <div style={{ fontSize: '0.85rem', color: PLANIVIO.textMuted }}>
                        {formatCurrency(selectedGoal.current_amount)} von {formatCurrency(selectedGoal.target_amount)}
                      </div>
                    </div>
                  </div>
                  <div style={{ 
                    padding: '0.5rem 0.75rem', 
                    background: 'white', 
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    color: PLANIVIO.teal,
                    fontWeight: 600
                  }}>
                    Noch benötigt: {formatCurrency(selectedGoal.remaining)}
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Betrag * (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    style={styles.formInput}
                    value={contributeAmount}
                    onChange={e => setContributeAmount(e.target.value)}
                    placeholder="50.00"
                    required
                    autoFocus
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  />
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.formLabel}>Notiz</label>
                  <input
                    type="text"
                    style={styles.formInput}
                    value={contributeNote}
                    onChange={e => setContributeNote(e.target.value)}
                    placeholder="z.B. Bonus vom Arbeitgeber"
                    onFocus={(e) => e.currentTarget.style.borderColor = PLANIVIO.teal}
                    onBlur={(e) => e.currentTarget.style.borderColor = PLANIVIO.border}
                  />
                </div>
              </div>

              <div style={styles.modalActions}>
                <button type="button" style={styles.btnSecondary} onClick={() => setShowContributeModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" style={styles.btnSuccess}>
                  💰 Einzahlen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Spinner Animation + Touch-Targets */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .savings-delete-mini::before,
        .savings-close-mini::before {
          content: '';
          position: absolute;
          inset: -6px;
        }
      `}</style>

    </div>
  );
};

export default SavingsGoals;

